import {
    autoUpdate,
    flip,
    FloatingPortal,
    Placement,
    shift,
    size,
    useFloating,
} from '@floating-ui/react';
import * as React from 'react';

interface FloatingDropdownProps {
    hasOptions: boolean;
    anchorRef: React.RefObject<HTMLInputElement | null>;
    dropdownId: string;
    dropdownAriaLabel: string;
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
    onFocus: (event: React.FocusEvent<HTMLDivElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLDivElement>) => void;
    onFloatingRefChange: (element: HTMLDivElement | null) => void;
    footer?: React.ReactNode;
    children: React.ReactNode;
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface AdvancedFilterTextDropdownInputHandle {
    /** Closes the popup, commits pending input once and removes focus from the input. */
    dismiss: () => void;
}

interface Props {
    value: string;
    options: string[];
    filterOptions: (inputValue: string, options: string[]) => string[];
    onValueChange: (value: string) => void;
    onCommitValue: (value: string) => void;
    onFocus?: () => void;
    placeholder?: string;
    title?: string;
    ariaLabel: string;
    dropdownAriaLabel: string;
    inputClassName?: string;
    inputType?: string;
    /** Initial search: options are hidden and the footer shows a loading text. */
    loading?: boolean;
    emptyText?: string;
    hasMoreOptions?: boolean;
    /** Continuation search: existing options are kept and further searches are blocked. */
    loadingMoreOptions?: boolean;
    loadMoreFailed?: boolean;
    onLoadMoreOptions?: () => void;
    ref?: React.Ref<AdvancedFilterTextDropdownInputHandle>;
    children?: React.ReactNode;
}

const FLOATING_DROPDOWN_VIEWPORT_PADDING = 8;
const FLOATING_DROPDOWN_MIN_LIST_HEIGHT_REM = 11;

/**
 * Sets the height limits of the popup and hides the resizer when they are equal. The
 * maximum is the content height and the space beside the input; until a resize locks the
 * side, the larger side is used so the cap never keeps `flip` from moving the popup.
 * floating-ui's availableHeight is not used, since the cross-axis shift makes it span the
 * whole viewport. The minimum reserves FLOATING_DROPDOWN_MIN_LIST_HEIGHT_REM for the list.
 */
const limitDropdownHeight = (
    floating: HTMLElement,
    anchor: HTMLElement,
    lockedPlacement: Placement | null
) => {
    const list = floating.querySelector('ul');
    if (!list) {
        return;
    }
    const floatingStyle = getComputedStyle(floating);
    const borderHeight =
        parseFloat(floatingStyle.borderTopWidth) +
        parseFloat(floatingStyle.borderBottomWidth);
    const footerHeight = list.nextElementSibling?.getBoundingClientRect().height ?? 0;
    const scrollbarHeight = list.offsetHeight - list.clientHeight;
    const contentHeight =
        list.scrollHeight + scrollbarHeight + footerHeight + borderHeight;

    const anchorRect = anchor.getBoundingClientRect();
    const spaceAbove = anchorRect.top - FLOATING_DROPDOWN_VIEWPORT_PADDING;
    const spaceBelow =
        floating.ownerDocument.documentElement.clientHeight -
        anchorRect.bottom -
        FLOATING_DROPDOWN_VIEWPORT_PADDING;
    const space = !lockedPlacement
        ? Math.max(spaceAbove, spaceBelow)
        : lockedPlacement.startsWith('top')
          ? spaceAbove
          : spaceBelow;

    const maxHeight = Math.max(0, Math.min(Math.ceil(contentHeight), space));
    const rem = parseFloat(
        getComputedStyle(floating.ownerDocument.documentElement).fontSize
    );
    const minHeight = list.children.length
        ? Math.min(
              FLOATING_DROPDOWN_MIN_LIST_HEIGHT_REM * rem +
                  scrollbarHeight +
                  footerHeight +
                  borderHeight,
              maxHeight
          )
        : 0;
    floating.style.maxHeight = `${maxHeight}px`;
    floating.style.minHeight = `${minHeight}px`;
    floating.style.resize =
        list.children.length && maxHeight > minHeight ? 'vertical' : 'none';
};

const FloatingDropdown = ({
    hasOptions,
    anchorRef,
    dropdownId,
    dropdownAriaLabel,
    onMouseDown,
    onFocus,
    onBlur,
    onFloatingRefChange,
    footer,
    children,
}: FloatingDropdownProps) => {
    /** Set once the user grabs the resizer; locked so the popup does not flip sides. */
    const [resizedPlacement, setResizedPlacement] = React.useState<Placement | null>(
        null
    );
    // floating-ui may keep an earlier `apply` closure, so it reads the lock from a ref.
    const resizedPlacementRef = React.useRef<Placement | null>(null);

    const { refs, floatingStyles, placement } = useFloating({
        open: true,
        placement: resizedPlacement ?? 'bottom-start',
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        middleware: [
            !resizedPlacement &&
                flip({
                    fallbackPlacements: ['top-start'],
                    padding: FLOATING_DROPDOWN_VIEWPORT_PADDING,
                }),
            shift({
                padding: FLOATING_DROPDOWN_VIEWPORT_PADDING,
                crossAxis: true,
            }),
            size({
                padding: FLOATING_DROPDOWN_VIEWPORT_PADDING,
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, {
                        minWidth: `${rects.reference.width}px`,
                        maxWidth: `min(30rem, calc(100vw - ${FLOATING_DROPDOWN_VIEWPORT_PADDING * 2}px))`,
                    });
                    if (anchorRef.current) {
                        limitDropdownHeight(
                            elements.floating,
                            anchorRef.current,
                            resizedPlacementRef.current
                        );
                    }
                },
            }),
        ],
    });

    React.useLayoutEffect(() => {
        if (anchorRef.current) {
            refs.setReference(anchorRef.current);
        }
    }, [anchorRef, refs]);

    // The options can change without the popup changing size, e.g. below a resized height.
    React.useLayoutEffect(() => {
        if (refs.floating.current && anchorRef.current) {
            limitDropdownHeight(
                refs.floating.current,
                anchorRef.current,
                resizedPlacementRef.current
            );
        }
    });

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        const element = event.currentTarget;
        // Only the native resizer (or the border) targets the popup itself.
        if (hasOptions && event.button === 0 && event.target === element) {
            // Keeps the current height when the list stops using its default max-height.
            element.style.height = `${element.getBoundingClientRect().height}px`;
            resizedPlacementRef.current = placement;
            setResizedPlacement(placement);
        }
        onMouseDown(event);
    };

    const setFloatingRef = React.useCallback(
        (element: HTMLDivElement | null) => {
            refs.setFloating(element);
            onFloatingRefChange(element);
        },
        [onFloatingRefChange, refs]
    );

    if (!anchorRef.current) {
        return null;
    }

    const className = `c-advanced-filter__text-dropdown${
        resizedPlacement ? ' c-advanced-filter__text-dropdown--resized' : ''
    }`;

    return (
        <FloatingPortal>
            <div
                ref={setFloatingRef}
                className={className}
                style={floatingStyles}
                onMouseDown={handleMouseDown}
                onFocus={onFocus}
                onBlur={onBlur}
            >
                <ul
                    id={dropdownId}
                    className="c-advanced-filter__text-dropdown-list"
                    role="listbox"
                    aria-label={dropdownAriaLabel}
                >
                    {children}
                </ul>
                {footer}
            </div>
        </FloatingPortal>
    );
};

export const AdvancedFilterTextDropdownInput: React.FC<Props> = ({
    value,
    options,
    filterOptions,
    onValueChange,
    onCommitValue,
    onFocus,
    placeholder,
    title,
    ariaLabel,
    dropdownAriaLabel,
    inputClassName,
    inputType = 'text',
    loading = false,
    emptyText = 'Finner ingen gyldige filter',
    hasMoreOptions = false,
    loadingMoreOptions = false,
    loadMoreFailed = false,
    onLoadMoreOptions,
    ref,
    children,
}) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const dropdownRef = React.useRef<HTMLDivElement | null>(null);
    const moreButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const suppressBlurCommitRef = React.useRef(false);
    const focusInDropdownRef = React.useRef(false);
    const restoreFocusRef = React.useRef<'open' | 'closed' | null>(null);
    const dropdownId = React.useId();
    const [dropdownVisible, setDropdownVisible] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
    const [hasTypedSinceFocus, setHasTypedSinceFocus] = React.useState(false);
    const filteredOptions = React.useMemo(
        () => (hasTypedSinceFocus ? filterOptions(value, options) : options),
        [filterOptions, hasTypedSinceFocus, options, value]
    );
    const noMatchingOptions = filteredOptions.length === 0;
    const showMoreButton = (hasMoreOptions || loadMoreFailed) && !loading;
    const showFooter = loading || showMoreButton || noMatchingOptions;
    // Read by blur handlers that run while the footer button is being removed.
    const showMoreButtonRef = React.useRef(showMoreButton);
    showMoreButtonRef.current = showMoreButton;

    React.useEffect(() => {
        setHighlightedIndex(-1);
    }, [options]);

    const isInDropdown = (target: EventTarget | null): boolean =>
        target instanceof Node && !!dropdownRef.current?.contains(target);

    const closeDropdown = React.useCallback(() => {
        setHasTypedSinceFocus(false);
        setDropdownVisible(false);
        setHighlightedIndex(-1);
    }, []);

    /** Moves focus back from the popup to the input without treating it as a new focus. */
    const returnFocusToInput = React.useCallback((keepOpen: boolean) => {
        restoreFocusRef.current = keepOpen ? 'open' : 'closed';
        focusInDropdownRef.current = false;
        inputRef.current?.focus();
    }, []);

    const blurWithoutCommit = React.useCallback(() => {
        const active = inputRef.current?.ownerDocument.activeElement;
        if (
            active instanceof HTMLElement &&
            (active === inputRef.current || dropdownRef.current?.contains(active))
        ) {
            suppressBlurCommitRef.current = true;
            active.blur();
            // Guards against environments where blur() does not dispatch a blur event.
            suppressBlurCommitRef.current = false;
            return true;
        }
        return false;
    }, []);

    // A focused footer button that disappears (all values loaded, or a retry of the
    // first batch started) would drop focus to the document; keep it in the input.
    React.useLayoutEffect(() => {
        if (!showMoreButton && focusInDropdownRef.current) {
            returnFocusToInput(true);
        }
    }, [returnFocusToInput, showMoreButton]);

    const getHighlightedOptionId = () =>
        highlightedIndex >= 0 ? `${dropdownId}-option-${highlightedIndex}` : undefined;

    const scrollHighlightedIntoView = React.useCallback(() => {
        const highlightedItem = dropdownRef.current?.querySelector('.highlighted');
        if (highlightedItem) {
            highlightedItem.scrollIntoView({ block: 'nearest' });
        }
    }, []);

    const selectValue = React.useCallback(
        (nextValue: string) => {
            onValueChange(nextValue);
            onCommitValue(nextValue);
            blurWithoutCommit();
            focusInDropdownRef.current = false;
            setDropdownVisible(false);
            setHighlightedIndex(-1);
        },
        [blurWithoutCommit, onCommitValue, onValueChange]
    );

    const dismiss = React.useCallback(() => {
        const active = inputRef.current?.ownerDocument.activeElement ?? null;
        if (active === inputRef.current || isInDropdown(active)) {
            onCommitValue(value);
            blurWithoutCommit();
        }
        focusInDropdownRef.current = false;
        closeDropdown();
    }, [blurWithoutCommit, closeDropdown, onCommitValue, value]);

    React.useImperativeHandle(ref, () => ({ dismiss }), [dismiss]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        onValueChange(nextValue);
        setHasTypedSinceFocus(true);
        setDropdownVisible(true);
        setHighlightedIndex(-1);
    };

    const handleInputFocus = () => {
        const restore = restoreFocusRef.current;
        restoreFocusRef.current = null;
        if (restore) {
            setDropdownVisible(restore === 'open');
            return;
        }
        onFocus?.();
        setHasTypedSinceFocus(false);
        setDropdownVisible(true);
        setHighlightedIndex(-1);
    };

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        if (suppressBlurCommitRef.current) {
            suppressBlurCommitRef.current = false;
            closeDropdown();
            return;
        }

        // Focus moved into the popup (the footer button); commit when it leaves it.
        if (isInDropdown(event.relatedTarget)) {
            return;
        }

        closeDropdown();
        onCommitValue(value);
    };

    const handleDropdownFocus = () => {
        focusInDropdownRef.current = true;
    };

    const handleDropdownBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        if (
            isInDropdown(event.relatedTarget) ||
            event.relatedTarget === inputRef.current
        ) {
            return;
        }
        if (suppressBlurCommitRef.current) {
            suppressBlurCommitRef.current = false;
            focusInDropdownRef.current = false;
            closeDropdown();
            return;
        }
        // Some browsers blur an element that is being removed; the layout effect above
        // returns focus to the input instead of closing the popup.
        if (!showMoreButtonRef.current && event.relatedTarget === null) {
            return;
        }
        focusInDropdownRef.current = false;
        closeDropdown();
        onCommitValue(value);
    };

    // The portalled popup must resume tab order relative to the input.
    const focusAfterInput = () => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        const focusables = Array.from(
            input.ownerDocument.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        ).filter(element => element === input || !isInDropdown(element));
        const next = focusables[focusables.indexOf(input) + 1];
        if (next) {
            next.focus();
        } else {
            (input.ownerDocument.activeElement as HTMLElement | null)?.blur();
        }
    };

    const handleMoreButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            returnFocusToInput(false);
        } else if (event.key === 'Tab') {
            event.preventDefault();
            if (event.shiftKey) {
                returnFocusToInput(true);
            } else {
                focusAfterInput();
            }
        }
    };

    const handleMoreButtonClick = () => {
        if (!loadingMoreOptions) {
            onLoadMoreOptions?.();
        }
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const itemCount = filteredOptions.length;

        switch (event.key) {
            case 'ArrowDown':
                if (itemCount === 0) {
                    return;
                }
                event.preventDefault();
                setDropdownVisible(true);
                setHighlightedIndex(currentIndex => {
                    const nextIndex = (currentIndex + 1) % itemCount;
                    requestAnimationFrame(scrollHighlightedIntoView);
                    return nextIndex;
                });
                break;
            case 'ArrowUp':
                if (itemCount === 0) {
                    return;
                }
                event.preventDefault();
                setDropdownVisible(true);
                setHighlightedIndex(currentIndex => {
                    const nextIndex = (currentIndex - 1 + itemCount) % itemCount;
                    requestAnimationFrame(scrollHighlightedIntoView);
                    return nextIndex;
                });
                break;
            case 'Enter':
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    event.preventDefault();
                    selectValue(filteredOptions[highlightedIndex]);
                    return;
                }
                suppressBlurCommitRef.current = true;
                setHasTypedSinceFocus(false);
                onCommitValue(value);
                setDropdownVisible(false);
                setHighlightedIndex(-1);
                inputRef.current?.blur();
                break;
            case 'Escape':
                setHasTypedSinceFocus(false);
                setDropdownVisible(false);
                setHighlightedIndex(-1);
                inputRef.current?.blur();
                break;
            case 'Tab':
                // The popup is portalled, so the footer button is not in the natural tab order.
                if (!event.shiftKey && dropdownVisible && moreButtonRef.current) {
                    event.preventDefault();
                    moreButtonRef.current.focus();
                }
                break;
            default:
                break;
        }
    };

    const handleDropdownMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) {
            event.preventDefault();
        }
    };

    const renderDropdownItems = () => {
        if (loading || noMatchingOptions) {
            return null;
        }

        return filteredOptions.map((option, index) => (
            <li
                key={`${option}-${index}`}
                id={`${dropdownId}-option-${index}`}
                className={`c-advanced-filter__text-dropdown-item ${
                    index === highlightedIndex ? 'highlighted' : ''
                }`}
                onClick={() => selectValue(option)}
                role="option"
                aria-selected={index === highlightedIndex}
            >
                {option}
            </li>
        ));
    };

    const getFooterMessage = () => {
        if (loading) {
            return 'Søker…';
        }
        if (loadMoreFailed && !loadingMoreOptions) {
            return options.length === 0
                ? 'Kunne ikke hente forslag.'
                : 'Kunne ikke hente flere verdier.';
        }
        if (noMatchingOptions) {
            return hasMoreOptions ? 'Ingen treff blant hentede verdier.' : emptyText;
        }
        return 'Flere verdier kan finnes.';
    };
    const footer = showFooter ? (
        <div className="c-advanced-filter__text-dropdown-footer">
            <span aria-live="polite">{getFooterMessage()}</span>
            {showMoreButton && (
                <button
                    ref={moreButtonRef}
                    type="button"
                    className="c-advanced-filter__text-dropdown-more"
                    onClick={handleMoreButtonClick}
                    onKeyDown={handleMoreButtonKeyDown}
                    // aria-disabled instead of disabled keeps keyboard focus on the button.
                    aria-disabled={loadingMoreOptions}
                    aria-busy={loadingMoreOptions}
                >
                    {loadingMoreOptions
                        ? 'Søker…'
                        : loadMoreFailed
                          ? 'Prøv igjen'
                          : 'Søk videre'}
                </button>
            )}
        </div>
    ) : null;

    return (
        <>
            <input
                ref={inputRef}
                type={inputType}
                className={inputClassName ?? 'c-advanced-filter__input'}
                placeholder={placeholder}
                value={value}
                title={title}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                aria-label={ariaLabel}
                aria-autocomplete="list"
                aria-controls={dropdownId}
                aria-expanded={dropdownVisible}
                aria-haspopup="listbox"
                aria-activedescendant={getHighlightedOptionId()}
            />
            {children}
            {/* Mounted per opening, so a resized height does not outlive the popup. */}
            {dropdownVisible && (
                <FloatingDropdown
                    hasOptions={!loading && !noMatchingOptions}
                    anchorRef={inputRef}
                    dropdownId={dropdownId}
                    dropdownAriaLabel={dropdownAriaLabel}
                    onMouseDown={handleDropdownMouseDown}
                    onFocus={handleDropdownFocus}
                    onBlur={handleDropdownBlur}
                    onFloatingRefChange={element => {
                        dropdownRef.current = element;
                    }}
                    footer={footer}
                >
                    {renderDropdownItems()}
                </FloatingDropdown>
            )}
        </>
    );
};
